import React, { FunctionComponent, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { isEmpty, toNumber } from 'lodash-es'

import {
  COMMON_MIXPANEL_EVENTS,
  COMMON_MIXPANEL_PROPERTIES,
  REGISTRATION_ENTRY_POINTS,
  WELLNESS_CHECK_IN_ENTRY_POINTS,
  WELLNESS_CHECK_IN_EVENTS,
  WELLNESS_CHECK_IN_PAGES,
  WELLNESS_CHECK_IN_PROPERTIES,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { PAGE_ROUTES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import { HowAreYouFeelingTodayOption } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/constants'
import { WellnessCheckInResults } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/types'
import {
  getInitialWellnessCheckinValues,
  getWellnessCheckInTotalNumPages,
  isValidWellnessCheckInResults,
} from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/utils'
import { WellnessCheckInForm } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/wellnessCheckInForm/WellnessCheckInForm'
import { WellnessCheckInHeader } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/wellnessCheckInHeader/WellnessCheckInHeader'
import { WellnessCheckInCopyrightFooter } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/wellnessCheckInResultsFooter/WellnessCheckInCopyrightFooter'

import {
  postWellnessCheckInResultsData,
  setWellnessCheckInFormValues,
  setWellnessCheckInResults,
} from './data/wellnessCheckInActions'
import { getWellnessCheckInEntryPoint, getWellnessCheckInFormValues } from './data/wellnessCheckInSelectors'
import { useGetLegalFooterProps } from '../../common/hooks/useGetLegalFooterProps'
import { useNavigateToWellnessCheckIn } from '../../common/hooks/useNavigateToWellnessCheckIn'
import { useTrackViewPage } from '../../common/hooks/useTrackViewPage'
import { useWellnessCheckIn } from '../../common/hooks/useWellnessCheckIn'
import { logToSumoLogic } from '../../common/utils/userUtils'
import { getDeviceUUID, getGlobalIsHydrated } from '../../data/appGlobals/appGlobalsSelectors'
import { getIsLyraSocialCareEnabled } from '../../data/customer/customerSelectors'
import { trackEventWithObj } from '../../data/mixpanel'
import { useAppDispatch } from '../../data/storeConfiguration/store'
import { getId, getIsUserLoggedIn } from '../../data/user/userSelectors'

const WellnessCheckInFormContainer: FunctionComponent = ({}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const socialCareEnabled = useSelector(getIsLyraSocialCareEnabled)
  const { showWellnessCheckIn, resetWellnessCheckInValues } = useWellnessCheckIn()
  const { navigateToWellnessCheckInResults } = useNavigateToWellnessCheckIn()
  const isLoggedIn = useSelector(getIsUserLoggedIn)
  const lyraId = useSelector(getId)
  const deviceUUID = useSelector(getDeviceUUID)
  const { pageIndex } = useParams()
  const [searchParams] = useSearchParams()
  const initialMoodOption =
    searchParams.get('initialMoodOption') !== null
      ? (searchParams.get('initialMoodOption') as HowAreYouFeelingTodayOption)
      : undefined
  const currentPage = toNumber(pageIndex)
  const totalPages = getWellnessCheckInTotalNumPages(socialCareEnabled)
  const validPageNumber = currentPage <= totalPages
  const legalFooterProps = useGetLegalFooterProps()
  const mixpanelPageName = Object.values(WELLNESS_CHECK_IN_PAGES)[currentPage]
  const wellnessCheckInFormValues = useSelector(getWellnessCheckInFormValues)
  const wellnessCheckInValues = isEmpty(wellnessCheckInFormValues)
    ? getInitialWellnessCheckinValues(socialCareEnabled)
    : wellnessCheckInFormValues
  const wellnessCheckInEntryPoint = useSelector(getWellnessCheckInEntryPoint)
  const isHydrated = useSelector(getGlobalIsHydrated)
  const isInitialMount = useRef(true)

  useTrackViewPage(
    mixpanelPageName,
    {
      [COMMON_MIXPANEL_PROPERTIES.ACTION]: COMMON_MIXPANEL_EVENTS.PAGE_LOAD,
      [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: wellnessCheckInEntryPoint,
    },
    { skipAppendPageToEventName: true },
  )

  useEffect(() => {
    if (!showWellnessCheckIn) {
      navigate('/')
    } else if (!validPageNumber) {
      navigate(`${PAGE_ROUTES.WELLNESS_CHECK_IN.DEFAULT}/1`)
    }
  }, [showWellnessCheckIn, navigate, validPageNumber])

  useEffect(() => {
    // Wait for redux-persist rehydration before deciding to reset — otherwise
    // a late REHYDRATE overwrites the cleared form values with the previously
    // persisted ones (React 19 effects can fire before async sessionStorage reads).
    if (!isHydrated || !isInitialMount.current) return
    if (currentPage === 1) {
      resetWellnessCheckInValues({ clearResults: false })
    }
    isInitialMount.current = false
  }, [currentPage, isHydrated, resetWellnessCheckInValues])

  const handleSubmit = async (values: object) => {
    const results = values as WellnessCheckInResults
    dispatch(setWellnessCheckInResults({ results, withinPastThreeMonths: true, date: new Date().toISOString() }))
    dispatch(
      trackEventWithObj({
        event: WELLNESS_CHECK_IN_EVENTS.ASSESSMENT_COMPLETION,
        [WELLNESS_CHECK_IN_PROPERTIES.ASSESSMENT_RESULTS]: values,
      }),
    )
    // This should only be for logged in flow, pre-registered will use the v2 token flow.
    if (isLoggedIn && lyraId) {
      try {
        if (isValidWellnessCheckInResults(results)) {
          await dispatch(
            postWellnessCheckInResultsData({ lyraId, requestBody: { deviceUUID, body: JSON.stringify(results) } }),
          ).unwrap()
        }
        navigateToWellnessCheckInResults(WELLNESS_CHECK_IN_ENTRY_POINTS.ASSESSMENT)
      } catch (error) {
        console.error(`Unable save wellness check in : ${error}`)
        if (error instanceof Error) {
          logToSumoLogic('postWellnessCheckInResult', lyraId, `Unable save wellness check in : ${error.message}`)
        }
      }
    } else {
      navigate(PAGE_ROUTES.WELLNESS_CHECK_IN.PREVIEW, {
        state: { mixpanelEntryPoint: REGISTRATION_ENTRY_POINTS.WELLNESS_CHECK_IN },
      })
    }
  }

  const handleCTAPress = (formValues: object) => {
    dispatch(
      trackEventWithObj({
        event: `click ${mixpanelPageName} - Continue`,
        response: formValues,
        [COMMON_MIXPANEL_PROPERTIES.ACTION]: COMMON_MIXPANEL_EVENTS.CLICK,
      }),
    )
    if (currentPage < totalPages) {
      navigate(`${PAGE_ROUTES.WELLNESS_CHECK_IN.DEFAULT}/${currentPage + 1}`)
    }
  }

  const handleBackPress = () => {
    if (currentPage === 1) {
      switch (wellnessCheckInEntryPoint) {
        case WELLNESS_CHECK_IN_ENTRY_POINTS.HOMEBASE:
          navigate(PAGE_ROUTES.HOMEBASE)
          break
        case WELLNESS_CHECK_IN_ENTRY_POINTS.INTEGRATED_BENEFITS_PAGE:
          navigate(PAGE_ROUTES.SEARCH_CARE.BENEFITS)
          break
        case WELLNESS_CHECK_IN_ENTRY_POINTS.RESULTS:
          navigateToWellnessCheckInResults(WELLNESS_CHECK_IN_ENTRY_POINTS.RESULTS)
          break
        default:
          navigate('/')
      }
    } else {
      navigate(`${PAGE_ROUTES.WELLNESS_CHECK_IN.DEFAULT}/${currentPage - 1}`)
    }
  }

  return (
    <>
      <WellnessCheckInHeader isQuestionnairePage questionnairePageNumber={currentPage} onBack={handleBackPress} />
      <WellnessCheckInForm
        currentPage={currentPage}
        totalPages={totalPages}
        socialCareEnabled={socialCareEnabled}
        onSubmit={handleSubmit}
        onCTAPress={handleCTAPress}
        currValues={wellnessCheckInValues}
        setCurrValues={(values: object) => dispatch(setWellnessCheckInFormValues(values))}
        initialMoodOption={
          (Object.values(HowAreYouFeelingTodayOption)[
            wellnessCheckInValues.howAreYouFeelingToday as number
          ] as HowAreYouFeelingTodayOption) ?? initialMoodOption
        }
      />
      <WellnessCheckInCopyrightFooter {...legalFooterProps} />
    </>
  )
}

export default WellnessCheckInFormContainer
